import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth, authHeaders, formatApiErrorDetail } from "@/contexts/AuthContext";
import AdminTopbar from "@/components/admin/AdminTopbar";
import {
  Shield,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Loader2,
  Inbox,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  login_success: { label: "Login OK", success: true, color: "#00E5A0" },
  login_bad_password: { label: "Bad password", success: false, color: "#FF2D9C" },
  login_locked: { label: "Locked out", success: false, color: "#FF2D9C" },
  password_ok_2fa_required: { label: "Password OK · 2FA pending", success: true, color: "#FFD500" },
  password_ok_setup_required: { label: "Password OK · setup pending", success: true, color: "#FFD500" },
  "2fa_failed": { label: "2FA failed", success: false, color: "#FF2D9C" },
  "2fa_setup_failed": { label: "2FA setup failed", success: false, color: "#FF2D9C" },
  "2fa_setup_completed": { label: "2FA enrolled", success: true, color: "#5B2BFF" },
  "2fa_disabled": { label: "2FA disabled", success: true, color: "#FF5B17" },
  "2fa_disable_failed": { label: "2FA disable failed", success: false, color: "#FF2D9C" },
};

function formatDate(d) {
  if (!d) return "—";
  const date = typeof d === "string" ? new Date(d) : d;
  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
}

function shortUA(ua) {
  if (!ua) return "—";
  const m = ua.match(/(Chrome|Firefox|Safari|Edge|Opera)[/\s]([\d.]+)/);
  const os = (ua.match(/\((.*?)\)/) || [])[1] || "";
  if (m) return `${m[1]} · ${os.split(";")[0] || ""}`.trim();
  return ua.slice(0, 60);
}

export default function AdminActivity() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/activity`, {
        headers: authHeaders(token),
        params: { limit: 200 },
      });
      setItems(data || []);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load"
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  const handleRefresh = () => {
    setRefreshing(true);
    load();
  };

  const successCount = items.filter((i) => i.success).length;
  const failedCount = items.length - successCount;

  return (
    <div
      data-testid="admin-activity-page"
      className="min-h-screen bg-[#F2EBDC] text-[#0A0A0A]"
    >
      <AdminTopbar />

      <main className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF2D9C] mb-2">
              ✶ Security · Activity log
            </div>
            <h1
              className="font-display"
              style={{ fontSize: "clamp(2.25rem, 5vw, 4rem)", lineHeight: 0.9 }}
            >
              Who tried to get in?
            </h1>
            <p className="font-mono text-xs text-[#0A0A0A]/70 mt-2 max-w-xl">
              The last 200 access events — successful logins, wrong passwords,
              2FA attempts, lockouts and 2FA enrolment.
            </p>
          </div>

          <div className="flex flex-wrap gap-3">
            <StatBadge
              label="OK"
              value={successCount}
              color="#00E5A0"
              icon={<CheckCircle2 className="w-3.5 h-3.5" />}
            />
            <StatBadge
              label="Failed"
              value={failedCount}
              color="#FF2D9C"
              textColor="#FAF5E8"
              icon={<XCircle className="w-3.5 h-3.5" />}
            />
            <button
              data-testid="activity-refresh"
              onClick={handleRefresh}
              disabled={refreshing}
              className="btn-outline disabled:opacity-60"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 ${refreshing ? "animate-spin" : ""}`}
              />
              Refresh
            </button>
          </div>
        </div>

        <div
          className="bg-[#FAF5E8] border-2 border-[#0A0A0A] rounded-2xl overflow-hidden"
          style={{ boxShadow: "6px 6px 0 #0A0A0A" }}
        >
          {loading ? (
            <div className="p-16 flex items-center justify-center font-mono text-xs uppercase tracking-[0.25em] text-[#0A0A0A]/50">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Loading…
            </div>
          ) : items.length === 0 ? (
            <div className="p-16 text-center" data-testid="activity-empty">
              <Inbox className="w-10 h-10 mx-auto text-[#0A0A0A]/30 mb-3" />
              <div className="font-display text-2xl">No activity yet</div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table
                className="w-full text-sm"
                data-testid="activity-table"
              >
                <thead className="bg-[#0A0A0A] text-[#FAF5E8]">
                  <tr className="font-mono text-[10px] uppercase tracking-[0.2em]">
                    <th className="px-5 py-3 text-left">When</th>
                    <th className="px-5 py-3 text-left">Event</th>
                    <th className="px-5 py-3 text-left">Email</th>
                    <th className="px-5 py-3 text-left">IP</th>
                    <th className="px-5 py-3 text-left">Browser / OS</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => {
                    const meta = KIND_META[it.kind] || {
                      label: it.kind,
                      success: it.success,
                      color: "#0A0A0A",
                    };
                    return (
                      <tr
                        key={`${it.created_at}-${i}`}
                        data-testid={`activity-row-${i}`}
                        className={`border-t border-[#0A0A0A]/10 ${
                          i % 2 === 0 ? "bg-[#FAF5E8]" : "bg-[#F2EBDC]"
                        }`}
                      >
                        <td className="px-5 py-3 font-mono text-xs whitespace-nowrap">
                          {formatDate(it.created_at)}
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <span
                            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 font-mono text-[10px] uppercase tracking-[0.18em]"
                            style={{
                              borderColor: meta.color,
                              background: meta.success ? meta.color : "transparent",
                              color: meta.success && meta.color !== "#FFD500" ? "#0A0A0A" : meta.color,
                            }}
                          >
                            {it.success ? (
                              <CheckCircle2 className="w-3 h-3" />
                            ) : (
                              <XCircle className="w-3 h-3" />
                            )}
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-5 py-3 font-mono text-xs">
                          {it.email && it.email !== "unknown" ? (
                            it.email
                          ) : (
                            <span className="text-[#0A0A0A]/30">—</span>
                          )}
                        </td>
                        <td className="px-5 py-3 font-mono text-xs">{it.ip}</td>
                        <td className="px-5 py-3 font-mono text-xs text-[#0A0A0A]/80">
                          {shortUA(it.user_agent)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="mt-6 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-[#0A0A0A]/60">
          <Shield className="w-3.5 h-3.5" />
          If you see an unfamiliar IP, change your admin password immediately.
        </div>
      </main>
    </div>
  );
}

function StatBadge({ label, value, color, icon, textColor }) {
  return (
    <div
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 font-mono text-[11px] uppercase tracking-[0.22em]"
      style={{
        background: color,
        borderColor: color,
        color: textColor || "#0A0A0A",
        boxShadow: "3px 3px 0 #0A0A0A",
      }}
    >
      {icon}
      <span className="font-display text-base normal-case tracking-normal">
        {value}
      </span>
      {label}
    </div>
  );
}
